import { Box, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import React, { useContext, useEffect, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { WhiteCheckbox, WhiteInput, WhiteLink } from './AppModals';
import FirebaseContext from '../../context/firebase';
import ModalContext from '../../context/modal';
import SnackBarContext from '../../context/snackbar';
import UserContext from '../../context/user';
import useValidators from '../../hooks/useValidators';
import { SubmitButton } from '../Form';
import { Role, User } from '../../../types';
import useUserApiRoutes from '../../hooks/api/useUserApiRoutes';

const friendlySignUpError = (e: any) => {
  switch (e?.code) {
    case 'auth/email-already-in-use':
      return 'An account already exists for this email address. Try logging in instead.';
    case 'auth/invalid-action-code':
    case 'auth/expired-action-code':
      return 'This invite link has expired or was already used. Please ask for a new invitation.';
    case 'auth/invalid-email':
      return 'Please use the email address the invitation was sent to.';
    case 'auth/weak-password':
      return 'Please choose a stronger password (at least 6 characters).';
    default:
      return e?.response?.data?.message?.replace(/^Error:\s*/, '') || e?.message || 'Something went wrong. Please try again.';
  }
};

interface MyFormValues extends Required<Pick<User, 'fullName' | 'email' | 'phoneNumber' | 'password'>> {
  passwordConfirm: string;
  acceptTermsAndConditions: boolean;
}

const SignUpModal = () => {
  const classes = useStyles();
  const { setDrawerOpen } = useContext(ModalContext);
  const { showSnack } = useContext(SnackBarContext);
  const { saveUser } = useContext(UserContext);
  const { firebaseApp } = useContext(FirebaseContext);
  const { createUser, updateUser } = useUserApiRoutes();
  const { isEmailValid, isPhoneNumberValid, normalisePhoneNumber, validatePasswords } = useValidators();
  const [signUpRole, setSignUpRole] = useState<Role>('OWNER');
  const [signUpError, setSignUpError] = useState('');
  // Teammate invite links carry the address they were issued for (stamped on
  // by the server) — prefill it and lock the field, since the link only works
  // for that email anyway.
  const [invitedEmail] = useState(() => new URLSearchParams(window.location.search).get('invitedEmail') || '');

  useEffect(() => {
    if (firebaseApp.auth().isSignInWithEmailLink(window.location.href)) {
      setSignUpRole('TEAMMATE');
      // Clean-slate sign-out for a fresh invitee only — never kick out a
      // user who is already signed in (e.g. leftover link params in the URL).
      if (!firebaseApp.auth().currentUser) {
        firebaseApp.auth().signOut();
      }
    }
  }, [firebaseApp, setSignUpRole]);

  const initialValues: MyFormValues = {
    fullName: '',
    email: invitedEmail,
    phoneNumber: '64',
    password: '',
    passwordConfirm: '',
    acceptTermsAndConditions: false,
  };
  const validate = (values: MyFormValues) => {
    const errors: { [Property in keyof Partial<MyFormValues>]: string } = { ...validatePasswords(values) };
    for (const key of Object.keys(values) as (keyof MyFormValues)[]) {
      if (!values[key]) {
        errors[key] = 'Required';
      }
    }
    if (values.email && !isEmailValid(values.email)) {
      errors.email = 'Invalid email address';
    } else if (values.phoneNumber && !isPhoneNumberValid(`+${values.phoneNumber}`)) {
      errors.phoneNumber = 'Invalid mobile number — include the country code, e.g. 6421 123 4567';
    } else if (values.password !== values.passwordConfirm) {
      errors.passwordConfirm = 'Password must match';
    }
    return errors;
  };
  const signUp = (values: MyFormValues, { setSubmitting }: FormikHelpers<MyFormValues>) => {
    async function runAsync() {
      setSignUpError('');
      try {
        // The field shows a fixed "+" and is pre-filled with 64, so users
        // often type "+64 021..." — normalise to E.164 so the same mobile is
        // never registered twice under different formatting.
        const phoneNumber = normalisePhoneNumber(`+${values.phoneNumber}`) || `+${values.phoneNumber}`;
        if (values.email) {
          if (signUpRole === 'TEAMMATE') {
            // signInWithEmailLink creates (and signs into) the Firebase
            // account for the invited address, so attach the chosen password
            // to that account — creating a second one with the same email
            // would fail with email-already-in-use.
            const { user } = await firebaseApp.auth().signInWithEmailLink(values.email, window.location.href);
            if (user) {
              // The invite created the VirtualStack account with only an
              // email — persist the rest of the signup details to it, and
              // push the result into the user context (which loaded before
              // this save, so it would otherwise show an empty profile).
              // Saved first: the server rejects a mobile that's already
              // registered, and that should stop the signup before the
              // password/profile are attached.
              const updatedUser = await updateUser({
                uid: user.uid,
                fullName: values.fullName,
                phoneNumber,
              });
              await user.updatePassword(values.password);
              await user.updateProfile({ displayName: values.fullName });
              saveUser(updatedUser);
            }
          } else {
            // Server-side this also rejects a mobile registered to another
            // account — before the Firebase account below is created.
            await createUser({ ...values, phoneNumber });
            // await verifyPhoneNumber(`+${values.phoneNumber}`, values.email);
            const { user } = await firebaseApp.auth().createUserWithEmailAndPassword(values.email, values.password);
            if (user) {
              await user.updateProfile({ displayName: values.fullName });
            }
          }
        }
        setSubmitting(false);
        if (signUpRole === 'TEAMMATE') {
          // The one-time sign-in link params are spent — drop them from the
          // URL, otherwise a refresh re-detects the link, reopens signup and
          // signs the brand-new user out again.
          window.history.replaceState(null, '', '/');
          // Invited teammates are already signed in, and clicking the invite
          // link implicitly verified their email — the "check your email
          // before you can sign in" screen would be wrong on both counts.
          setDrawerOpen(undefined);
          showSnack('Welcome to the team! Verify your phone number to complete your signup.', 'success');
        } else {
          setDrawerOpen('signup-confirmed');
        }
      } catch (error) {
        setSubmitting(false);
        setSignUpError(friendlySignUpError(error));
      }
    }
    if (firebaseApp && values.phoneNumber) {
      runAsync();
    }
  };

  return (
    <Box>
      <Typography variant="h4">{signUpRole === 'TEAMMATE' ? 'Sign up to join your team!' : 'Sign Up!'}</Typography>
      <Formik initialValues={initialValues} validate={validate} onSubmit={signUp}>
        {({
          values,
          handleChange,
          handleSubmit,
          /* and other goodies */
        }) => (
          <form onSubmit={handleSubmit}>
            <WhiteInput label="Name" type="text" name="fullName" />
            <WhiteInput
              label="Email"
              type="email"
              name="email"
              disabled={signUpRole === 'TEAMMATE' && !!invitedEmail}
            />
            <WhiteInput label="Mobile #" type="text" name="phoneNumber" InputProps={{ startAdornment: '+' }} />
            <WhiteInput label="Password" type="password" name="password" />
            <WhiteInput label="Confirm Password" type="password" name="passwordConfirm" />
            <WhiteCheckbox
              label={
                <Typography>
                  I agree to the <WhiteLink href="#">Terms and Conditions</WhiteLink>
                </Typography>
              }
              checked={values.acceptTermsAndConditions}
              onChange={handleChange}
              name="acceptTermsAndConditions"
            />
            {signUpError ? (
              <Box mt={2} px={2} py={1.5} bgcolor="rgba(255,255,255,0.92)" borderRadius="8px">
                <Typography variant="body2" color="error" fontWeight={600}>
                  {signUpError}
                </Typography>
              </Box>
            ) : null}
            <Box display="flex" marginTop={3}>
              <SubmitButton classes={{ root: classes.buttonRoot }}>Submit</SubmitButton>
            </Box>
          </form>
        )}
      </Formik>
      <Box fontStyle="italic" lineHeight={1.4} className={classes.marginTop}>
        <Box>On submit, you will receive a</Box>
        {signUpRole === 'TEAMMATE' ? (
          <>
            <Box>confirmation mobile code.</Box>
            <Box>Please verify before proceeding.</Box>
          </>
        ) : (
          <>
            <Box>confirmation email & mobile code.</Box>
            <Box>Please verify both before proceeding.</Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SignUpModal;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    marginTop: {
      marginTop: theme.spacing(5),
    },
    buttonRoot: {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.info.main,
      marginRight: theme.spacing(2.25),
    },
  })
);
